import type { SecretInputValue } from '@/components/atoms/SecretInput';
import {
    type FieldValue,
    type SecretName,
    type SecretsPatch,
    sameStoredValue,
    storedValue,
} from '@/domain/providerConfig';

import type { FormFieldValue } from './formValues';

/** A form value as the server stores it (storedValue), so a stray space is not an edit. */
export function normalizeValue(value: FormFieldValue | FieldValue | undefined): FieldValue {
    return storedValue(value);
}

export function sameValue(
    a: FormFieldValue | FieldValue | undefined,
    b: FormFieldValue | FieldValue | undefined
): boolean {
    return sameStoredValue(a, b);
}

/** A blank replacement is nothing typed, so it leaves the stored secret as it is. */
export function secretChange(value: SecretInputValue | undefined): string | null | undefined {
    if (value === undefined || value.mode === 'keep') return undefined;
    if (value.mode === 'clear') return null;
    return value.value.trim() === '' ? undefined : value.value;
}

export function sameSecret(
    a: SecretInputValue | undefined,
    b: SecretInputValue | undefined
): boolean {
    return secretChange(a) === secretChange(b);
}

/** Only the secrets the schema has; any other name would be refused as unknownField. */
export function secretsPatchOf(
    secrets: Readonly<Record<SecretName, SecretInputValue>>,
    names: readonly SecretName[]
): SecretsPatch {
    const patch: SecretsPatch = {};
    for (const name of names) {
        const change = secretChange(secrets[name]);
        if (change !== undefined) patch[name] = change;
    }
    return patch;
}

export function hasTypedSecret(patch: SecretsPatch): boolean {
    return Object.values(patch).some((value) => typeof value === 'string');
}

export function hasClearedSecret(patch: SecretsPatch): boolean {
    return Object.values(patch).some((value) => value === null);
}
